import AddProductRouter from '@/pages/dashboard/AddProductRouter';
import BusinessCreateGate from '@/pages/dashboard/BusinessCreateGate';
import { isBusinessHeaderRefused } from '@/consts/businessForbidden';
import {
  DashboardHomeGate,
  DashboardIndexRedirect,
  DashboardV2Gate,
  OperationHealthGate,
} from '@/pages/dashboard/DashboardHomeGate';
import { ProductResumeLegacyRedirect } from '@/routes/productResumeRouteUtils';
import { createProductResumeShellChildren } from '@/routes/productResumeRoutes';
import { MySalesMovedTabRedirect, mySalesLegacyRoutes } from '@/routes/mySalesLegacyRoutes';
import { partnerLinkRoute } from '@/routes/partnerLinkRoute';
import { studentIndexRoute } from '@/routes/studentIndexRoute';
import { welcomeRoute } from '@/routes/welcomeRoute';
import BankingAccountGuard from '@/routes/BankingAccountGuard';
import DigitalAccountOpeningGuard from '@/routes/DigitalAccountOpeningGuard';
import VerifyModal from '@/sections/auth/VerifyModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthProvider } from '@/auth/JwtContext';
import { SessionProvider } from '@/auth/SessionContext';
import { useSessionContext } from '@/auth/useSessionContext';
import { useSnackbar } from 'notistack';
import { useNotify } from '@/components/snackbar';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router-dom';
// auth
import PartnersAccessGuard from '@/auth/PartnersAccessGuard';
import NfseFeatureGuard from '@/auth/NfseFeatureGuard';
import CaktoAnalyticsGuard from '@/auth/CaktoAnalyticsGuard';
import { parseResponseError } from '@/utils/api';
import { createQueryErrorToast } from '@/utils/queryErrorToast';
import { COMPANY_STATUS_LABELS } from '@/consts/financial';
import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
import ImpersonationGuard from '../auth/ImpersonationGuard';
import ProfileAccountGuard from '../auth/ProfileAccountGuard';
// layouts
import CompactLayout from '../layouts/compact';
import DashboardLayout from '../layouts/dashboard';
import MainLayout from '../layouts/main';
import PublicLayout from '../layouts/public';
import SimpleLayout from '../layouts/simple';
// config
import { PATH_DASHBOARD } from './paths';
//
import AffiliatesProvider from '../contexts/AffiliatesContext';
import BaasAccountProvider from '../contexts/BaasAccountContext';
import { CheckoutBuilderProvider } from '../contexts/CheckoutBuilderContext';
import EditProductProvider from '../contexts/EditProductContext';
import CoursesProvider from '../contexts/MemberCoursesContext';
import NotificationsProvider from '../contexts/NotificationsContext';
import OrdersProvider from '../contexts/OrdersContext';
import ProductModulesProvider from '../contexts/ProductModulesContext';
import ProductsProvider from '../contexts/ProductsContext';
import ReportAffiliateProvider from '../contexts/ReportAffiliateContext';
import ReportBallanceProvider from '../contexts/ReportBalanceContext';
import ReportCoProductionsProvider from '../contexts/ReportCoProductionsContext';
import ReportProductsProvider from '../contexts/ReportProductsContext';
import SubscriptionsProvider from '../contexts/SubscriptionContext';
import WithdrawalsProvider from '../contexts/WithdrawalsContext';
import { ImpersonationProvider } from '../contexts/ImpersonationContext';
import CheckoutBuilderLayout from '../layouts/checkout-builder/CheckoutBuilderLayout';
import StudentLayout from '../layouts/dashboard/StudentLayout';
import WatchCourse from '../pages/course/CourseWatch';
import CaktoAPI from '../pages/dashboard/CaktoApi';

import ProductRatings from '../pages/dashboard/ProductRatings';
import Webhooks from '../pages/dashboard/Webhooks';
import StudentNotifications from '../pages/student/StudentNotifications';
import {
  AboutPage,
  McpPage,
  CaktoMcpPage,
  ComplianceMembersV3Page,
  AddFees,
  AffiliateInvitePage,
  AffiliatesPage,
  AnotherAccountsPage,
  Apps,
  AwardsPage,
  BankingAccount,
  BankingHome,
  CheckoutBuilder,
  ComingSoonPage,
  Contact,
  CouponsPage,
  PartnersPage,
  Courses,
  CreatePasswordPage,
  EditProduct,
  Financial,
  FinancialCalendar,
  FinancialCreditCard,
  FinancialCreditCardTransactions,
  FinancialInvoicesDashboard,
  FinancialInvoicesAvulsa,
  FinancialInvoicesEdit,
  FinancialInvoicesRegister,
  FinancialInvoicesSettings,
  FinancialPixKeys,
  // Auth
  LoginPage,
  MaintenancePage,
  // Members,
  MembersDiscordFinishConfigPage,
  MySales,
  MySalesDisputeDetail,
  MySalesDisputes,
  NewPasswordPage,
  NotificationsPage,
  Page403,
  Page404,
  //
  Page500,
  PaymentPage,
  PricingPage,
  ProductMembersArea,
  ProductResumeShell,
  Products,
  Profile,
  // Rating Survey
  RatingSurveyPage,
  ShowcaseProducts,
  CaktoAnalyticsAccountPage,
  // Refund
  RefundHome,
  RefundPurchase,
  RefundPurchases,
  SubscriberHome,
  SubscriberSubscriptions,
  SubscriberPayment,
  SubscriberPaymentSuccess,
  SubscriberHistory,
  RegisterPage,
  ReportAbandonedSales,
  ReportAffiliatesRevenue,
  ReportCoProductionRevenue,
  ReportCoursesEngagement,
  ReportFinancialEstimate,
  ReportProductsRevenue,
  Reports,
  ReportWhatsappRecovery,
  ResetPasswordPage,
  StudentProfile,
  SubscriptionsTable,
  BusinessTeamPage,
  AccountDetailsPage,
  TeamPage,
  VerifyCodePage,
  BankingCreditCards,
  BankingCreditCardTransactions,
  BankingBoletoAssistant,
  BankingBoletoPayment,
  BankingCreditCardCreation,
  BankingTED,
  BankingMobileRecharge,
  BankingGiftCard,
  FinancialCreateBankingAccount,
  WebhookLogs,
  WebhookSettings,
  WebhooksRecentActivities,
  MarketplaceLayout,
  MarketplaceHomePage,
  MarketplaceSearchPage,
  MarketplaceProductPage,
  MarketplaceFavoritesPage,
  MarketplaceSellerPage,
} from './elements';
// ----------------------------------------------------------------------

function AuthenticatedRoutesProvider({ children }) {
  return (
    <AuthProvider>
      <SessionProvider>
        <ImpersonationProvider>
          {children || <Outlet />}
        </ImpersonationProvider>
      </SessionProvider>
    </AuthProvider>
  );
}

AuthenticatedRoutesProvider.propTypes = {
  children: PropTypes.node,
};

function DashboardRouteLayout() {
  const queryClient = useQueryClient();
  const { refreshUser, closeVerificationModal } = useSessionContext();

  const { data: isOpenVerifyModal = false } = useQuery({
    queryKey: ['isOpenVerifyModal'],
    queryFn: () => queryClient.getQueryData(['isOpenVerifyModal']) ?? false,
    enabled: false,
    initialData: () => queryClient.getQueryData(['isOpenVerifyModal']) ?? false,
    staleTime: Infinity,
  });

  const { data: verificationStatus } = useQuery({
    queryKey: ['verificationStatus'],
    queryFn: () => queryClient.getQueryData(['verificationStatus']) ?? null,
    enabled: false,
    initialData: () => queryClient.getQueryData(['verificationStatus']) ?? null,
    staleTime: Infinity,
  });

  const onCloseVerifyLoginModal = () => {
    closeVerificationModal();
  };

  const onSuccessVerification = async () => {
    try {
      await refreshUser({ forceUpdate: true });
    } catch {
      // refreshUser limpa flags em caso de 401; em outros erros mantemos modal
    }
  };

  return (
    <AuthGuard>
      <ImpersonationGuard>
        <>
          <DashboardLayout />
          <VerifyModal
            open={isOpenVerifyModal}
            onClose={onCloseVerifyLoginModal}
            isToValidateEmail={Boolean(verificationStatus?.isToValidateEmail)}
            isToValidateWhatsapp={Boolean(verificationStatus?.isToValidateWhatsapp)}
            isToLogoutOnClose
            onSuccessWhatsapp={onSuccessVerification}
          />
        </>
      </ImpersonationGuard>
    </AuthGuard>
  );
}

const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const getCompanyDataBoolean = (data, keys = []) =>
  keys.some((key) => isTruthyFlag(data?.[key]));

const BLOCKED_FINANCIAL_ACTION_TOOLTIP =
  'Sua conta está bloqueada. Fale com nosso time de Suporte.';

function FinancialBlockedActionRouteGuard({ children }) {
  const { user } = useSessionContext();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  const blockedWithoutDigitalAccount = getCompanyDataBoolean(user, [
    'isAccountBlockedWithoutDigitalAccount',
    'is_account_blocked_without_digital_account',
    'isBlockedWithoutDigitalAccount',
    'blockedWithoutDigitalAccount',
    'blocked_without_digital_account',
  ]);
  const isAccountBlocked = getCompanyDataBoolean(user, [
    'accountBlocked',
    'account_blocked',
    'isAccountBlocked',
    'is_account_blocked',
  ]);
  const companyStatus =
    user?.companyStatus || user?.company?.status || user?.status;
  const isBlockedFinancially =
    companyStatus === COMPANY_STATUS_LABELS.BLOCKED ||
    blockedWithoutDigitalAccount ||
    isAccountBlocked;

  useEffect(() => {
    if (!isBlockedFinancially) return;

    enqueueSnackbar(BLOCKED_FINANCIAL_ACTION_TOOLTIP, {
      variant: 'warning',
      autoHideDuration: 5000,
    });
  }, [enqueueSnackbar, isBlockedFinancially]);

  if (!isBlockedFinancially) {
    return children;
  }

  return <Navigate to={PATH_DASHBOARD.general.financial} state={{ from: location.pathname }} replace />;
}

FinancialBlockedActionRouteGuard.propTypes = {
  children: PropTypes.node,
};

export default function Router() {
  const queryClient = useQueryClient();

  const notify = useNotify();

  const location = useLocation();

  useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
        retry: false,
        onError: createQueryErrorToast(notify),
      },
      mutations: {
        onError: (error) => {
          const currentPath = window.location.pathname;
          const isPublicRoute =
            currentPath.startsWith('/mcp') ||
            currentPath.startsWith('/refund') ||
            currentPath.startsWith('/subscriptions') ||
            currentPath.startsWith('/auth') ||
            currentPath.startsWith('/survey') ||
            currentPath.startsWith('/p/');

          const status = error?.response?.status;
          const isUnauthorizedError = status === 401 || status === 403;

          if (isPublicRoute && isUnauthorizedError) {
            return;
          }

          // O BusinessContext já avisa e volta ao negócio padrão (CBK-572); um segundo
          // aviso com a frase do backend seria o mesmo erro contado duas vezes.
          if (isBusinessHeaderRefused(error)) {
            return;
          }

          const { message: errorMessage } = parseResponseError(error);
          notify.error(errorMessage);
        },
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname.includes('dashboard')) {
      localStorage.setItem('next', location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  return useRoutes([
    // Auth
    {
      path: 'auth',
      element: <AuthenticatedRoutesProvider />,
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <LoginPage /> },
        { path: 'register-unprotected', element: <RegisterPage /> },
        {
          element: <CompactLayout />,
          children: [
            { path: 'reset-password', element: <ResetPasswordPage /> },
            { path: 'new-password', element: <NewPasswordPage /> },
            {
              path: 'create-password',
              element: <CreatePasswordPage />,
            },
            { path: 'verify', element: <VerifyCodePage /> },
          ],
        },
      ],
    },

    {
      // Sem AuthGuard de propósito: o convite chega por app de mensagem, a quem muitas vezes
      // não tem sessão ali (navegador interno do app, ou ainda sem conta). O login é pedido só
      // ao se afiliar, e volta pro convite (CKTPL-1418).
      path: 'affiliate/invite/:id',
      element: (
        <AuthenticatedRoutesProvider>
          <AffiliateInvitePage />
        </AuthenticatedRoutesProvider>
      ),
    },

    partnerLinkRoute,

    welcomeRoute,

    {
      path: 'student',
      element: (
        <AuthenticatedRoutesProvider>
          <AuthGuard>
            <StudentLayout />
          </AuthGuard>
        </AuthenticatedRoutesProvider>
      ),
      children: [
        studentIndexRoute,
        {
          path: 'profile',
          element: <StudentProfile />,
        },
        {
          path: 'notifications',
          element: (
            <NotificationsProvider>
              <StudentNotifications />
            </NotificationsProvider>
          ),
        },
        {
          path: 'courses',
          element: (
            <CoursesProvider>
              <Courses />
            </CoursesProvider>
          ),
        },
        {
          path: 'courses/:course_id',
          element: <WatchCourse />,
        },
      ],
    },

    // Dashboard
    {
      path: 'dashboard',
      element: (
        <AuthenticatedRoutesProvider>
          <DashboardRouteLayout />
        </AuthenticatedRoutesProvider>
      ),
      children: [
        { element: <DashboardIndexRedirect />, index: true },
        { path: 'home', element: <DashboardHomeGate /> },
        { path: 'operation-health', element: <OperationHealthGate /> },
        { path: 'home-v2', element: <DashboardV2Gate /> },
        {
          path: 'analytics',
          element: (
            <CaktoAnalyticsGuard>
              <CaktoAnalyticsAccountPage />
            </CaktoAnalyticsGuard>
          ),
        },
        {
          path: 'my-sales',
          element: (
            <MySalesMovedTabRedirect>
              <OrdersProvider>
                <MySales />
              </OrdersProvider>
            </MySalesMovedTabRedirect>
          ),
        },
        {
          // Item Reembolso do submenu: a página de Minhas Vendas, que nesta rota se
          // apresenta como tela própria. O path é próprio porque `isPathActive`
          // descarta a querystring, e o submenu precisa dele para marcar o item ativo.
          path: 'my-sales/refund-requests',
          element: (
            <OrdersProvider>
              <MySales />
            </OrdersProvider>
          ),
        },
        // Chargeback e MED: tela própria, fora do OrdersProvider (a lista vem de
        // `disputes/`, não de `/orders/`). O e-mail de chargeback/MED aponta para o
        // detalhe, por isso o `refId` fica no path e não na querystring.
        { path: 'my-sales/disputes', element: <MySalesDisputes /> },
        { path: 'my-sales/disputes/:refId', element: <MySalesDisputeDetail /> },
        // Relatórios e Cupons: itens do submenu de Minhas vendas, em tela própria e fora do
        // OrdersProvider, porque não listam vendas. O prefixo `my-sales/` é o que acende o
        // título Minhas vendas no menu junto com o item.
        { path: 'my-sales/reports', element: <Reports /> },
        { path: 'my-sales/coupons', element: <CouponsPage /> },
        {
          path: 'subscriptions',
          element: (
            <SubscriptionsProvider>
              <SubscriptionsTable />
            </SubscriptionsProvider>
          ),
        },
        {
          path: 'financial',
          element: (
            <ImpersonationGuard allowImpersonating={false}>
              <WithdrawalsProvider>
                <BaasAccountProvider>
                  <Outlet />
                </BaasAccountProvider>
              </WithdrawalsProvider>
            </ImpersonationGuard>
          ),
          children: [
            {
              index: true,
              element: <Financial />,
            },
            {
              path: 'calendar',
              element: (
                <FinancialBlockedActionRouteGuard>
                  <FinancialCalendar />
                </FinancialBlockedActionRouteGuard>
              ),
            },
            {
              path: 'credit-card',
              element: (
                <FinancialBlockedActionRouteGuard>
                  <FinancialCreditCard />
                </FinancialBlockedActionRouteGuard>
              ),
            },
            {
              path: 'credit-card/:id',
              element: (
                <FinancialBlockedActionRouteGuard>
                  <FinancialCreditCardTransactions />
                </FinancialBlockedActionRouteGuard>
              ),
            },
            {
              element: <BankingAccountGuard />,
              children: [
                {
                  path: 'pix-keys',
                  element: (
                    <FinancialBlockedActionRouteGuard>
                      <FinancialPixKeys />
                    </FinancialBlockedActionRouteGuard>
                  ),
                },
              ],
            },
            {
              element: <DigitalAccountOpeningGuard />,
              children: [
                {
                  path: 'banking-account',
                  element: <BankingAccount />,
                },
              ],
            },
          ],
        },
        {
          path: 'fiscal',
          element: (
            <ImpersonationGuard allowImpersonating={false}>
              <NfseFeatureGuard>
                <Outlet />
              </NfseFeatureGuard>
            </ImpersonationGuard>
          ),
          children: [
            { path: 'invoices', element: <FinancialInvoicesDashboard /> },
            { path: 'invoices/avulsa', element: <FinancialInvoicesAvulsa /> },
            { path: 'settings', element: <FinancialInvoicesSettings /> },
            { path: 'settings/edit', element: <FinancialInvoicesEdit /> },
            { path: 'settings/register', element: <FinancialInvoicesRegister /> },
            // Endereços de antes de a listagem entrar no dashboard e a configuração
            // ganhar item próprio no menu. Sem eles, favorito antigo cai no 404.
            {
              path: 'invoices/all',
              element: <Navigate to={PATH_DASHBOARD.fiscal.financialInvoices} replace />,
            },
            {
              path: 'invoices/edit',
              element: <Navigate to={PATH_DASHBOARD.fiscal.financialInvoicesEdit} replace />,
            },
            {
              path: 'invoices/register',
              element: <Navigate to={PATH_DASHBOARD.fiscal.financialInvoicesRegister} replace />,
            },
          ],
        },
        {
          path: 'banking',
          // O guard vem antes dos providers, que já buscam a conta e os saques do dono ao montar.
          element: (
            <ImpersonationGuard allowImpersonating={false}>
              <BaasAccountProvider>
                <WithdrawalsProvider>
                  <Outlet />
                </WithdrawalsProvider>
              </BaasAccountProvider>
            </ImpersonationGuard>
          ),
          children: [
            {
              element: <DigitalAccountOpeningGuard />,
              children: [
                {
                  path: 'create-account/cnpj',
                  element: <FinancialCreateBankingAccount />,
                },
              ],
            },
            {
              element: <BankingAccountGuard />,
              children: [
                { index: true, element: <BankingHome /> },
                { path: 'credit-cards', element: <BankingCreditCards /> },
                { path: 'credit-cards/:id', element: <BankingCreditCardTransactions /> },
                { path: 'credit-cards/new', element: <BankingCreditCardCreation /> },
                { path: 'boleto/assistant', element: <BankingBoletoAssistant /> },
                {
                  path: 'boleto/payment',
                  element: (
                    <FinancialBlockedActionRouteGuard>
                      <BankingBoletoPayment />
                    </FinancialBlockedActionRouteGuard>
                  ),
                },
                { path: 'ted', element: <BankingTED /> },
                { path: 'mobile-recharge', element: <BankingMobileRecharge /> },
                { path: 'gift-card', element: <BankingGiftCard /> },
              ],
            },
          ],
        },
        { path: 'apps', element: <Apps /> },
        {
          path: 'webhooks',
          element: (           
              <Outlet />            
          ),
          children: [
            { index: true, element: <Webhooks /> },
            { path: ':webhookId/logs', element: <WebhookLogs /> },
            { path: ':webhookId/settings', element: <WebhookSettings /> },
            { path: 'activities', element: <WebhooksRecentActivities /> },
          ],
        },
        {
          path: 'profile',
          element: (
            <ProfileAccountGuard>
              <Profile />
            </ProfileAccountGuard>
          ),
        },
        {
          path: 'add-fees',
          element: (
            <ImpersonationGuard allowImpersonating={false}>
              <AddFees />
            </ImpersonationGuard>
          ),
        },
        {
          path: 'cakto-api',
          element: (
            <ImpersonationGuard allowImpersonating={false}>
              <CaktoAPI />
            </ImpersonationGuard>
          ),
        },
        {
          path: 'cakto-mcp',
          element: (
            <ImpersonationGuard allowImpersonating={false}>
              <CaktoMcpPage />
            </ImpersonationGuard>
          ),
        },
        {
          path: 'compliance/members-v3',
          element: (
            <ImpersonationGuard allowImpersonating={false}>
              <ComplianceMembersV3Page />
            </ImpersonationGuard>
          ),
        },
        {
          path: 'notifications',
          element: (
            <NotificationsProvider>
              <NotificationsPage />
            </NotificationsProvider>
          ),
        },

        {
          path: 'affiliates',
          element: (
            <AffiliatesProvider type="affiliates">
              <AffiliatesPage />
            </AffiliatesProvider>
          ),
        },
        {
          path: 'products',
          element: (
            <ProductsProvider>
              <Products />
            </ProductsProvider>
          ),
        },
        {
          path: 'products/add-product',
          element: <AddProductRouter />,
        },
        {
          path: 'products/resume/:id/*',
          element: <ProductResumeLegacyRedirect />,
        },
        {
          path: 'products/resume-shell/:id',
          element: <ProductResumeShell />,
          children: createProductResumeShellChildren(),
        },
        {
          path: 'products/:id',
          element: (
            <EditProductProvider>
              <Outlet />
            </EditProductProvider>
          ),
          children: [
            {
              path: 'affiliates/:short_id',
              element: <EditProduct />,
            },
            {
              path: 'edit',
              element: <EditProduct />,
            },
            {
              path: 'members-area',
              element: (
                <ProductModulesProvider>
                  <ProductMembersArea />
                </ProductModulesProvider>
              ),
            },
            {
              path: 'ratings',
              element: <ProductRatings />,
            },
          ],
        },
        {
          path: 'products/:id/:short_user_id',
          element: (
            <EditProductProvider>
              <Outlet />
            </EditProductProvider>
          ),
          children: [
            {
              path: 'edit',
              element: <EditProduct />,
            },
            {
              path: 'members-area',
              element: <ProductMembersArea />,
            },
          ],
        },
        ...mySalesLegacyRoutes,
        { path: 'reports/abandoned-sales', element: <ReportAbandonedSales /> },
        { path: 'reports/whatsapp-recovery', element: <ReportWhatsappRecovery /> },
        {
          path: 'reports/affiliates-revenue',
          element: (
            <ReportAffiliateProvider>
              <ReportAffiliatesRevenue />
            </ReportAffiliateProvider>
          ),
        },
        {
          path: 'reports/coproduction-revenue',
          element: (
            <ReportCoProductionsProvider>
              <ReportCoProductionRevenue />
            </ReportCoProductionsProvider>
          ),
        },
        { path: 'reports/courses-engagement', element: <ReportCoursesEngagement /> },
        {
          path: 'reports/financial-estimate',
          element: (
            <ReportBallanceProvider>
              <ReportFinancialEstimate />
            </ReportBallanceProvider>
          ),
        },
        {
          path: 'reports/products-revenue',
          element: (
            <ReportProductsProvider>
              <ReportProductsRevenue />
            </ReportProductsProvider>
          ),
        },
        {
          path: 'partners',
          element: (
            <PartnersAccessGuard>
              <PartnersPage />
            </PartnersAccessGuard>
          ),
        },
        {
          path: 'showcase',
          element: <ShowcaseProducts />,
        },
        {
          path: 'businesses/new',
          element: <BusinessCreateGate />,
        },
        {
          path: 'team',
          element: (
            <ImpersonationGuard allowImpersonating={false}>
              <TeamPage />
            </ImpersonationGuard>
          ),
        },
        {
          path: 'businesses/team',
          element: (
            <ImpersonationGuard allowImpersonating={false}>
              <BusinessTeamPage />
            </ImpersonationGuard>
          ),
        },
        {
          path: 'businesses/account',
          element: <AccountDetailsPage />,
        },
        {
          path: 'another-accounts',
          element: <AnotherAccountsPage />,
        },
        {
          path: 'awards',
          element: <AwardsPage />,
        },
      ],
    },

    {
      element: (
        <AuthenticatedRoutesProvider>
          {/* O guard fica por fora do provider: a busca do checkout não pode sair antes de a
              sessão ser conferida, ou o visitante leva o 403 em toast no editor vazio. */}
          <AuthGuard returnAfterLogin>
            <CheckoutBuilderProvider>
              <CheckoutBuilderLayout />
            </CheckoutBuilderProvider>
          </AuthGuard>
        </AuthenticatedRoutesProvider>
      ),
      children: [
        {
          path: 'checkout-builder/:id',
          element: <CheckoutBuilder />,
        },
      ],
    },

    // Cakto Marketplace: vitrine pública, sem login. A compra sai para o checkout do produtor.
    {
      path: 'marketplace',
      element: <MarketplaceLayout />,
      children: [
        { index: true, element: <MarketplaceHomePage /> },
        { path: 'busca', element: <MarketplaceSearchPage /> },
        { path: 'produto/:id', element: <MarketplaceProductPage /> },
        { path: 'produtor/:slug', element: <MarketplaceSellerPage /> },
        { path: 'favoritos', element: <MarketplaceFavoritesPage /> },
      ],
    },

    // Public Routes
    {
      element: <PublicLayout />,
      children: [
        {
          element: <MainLayout />,
          children: [
            { element: <Navigate to="/dashboard/home" replace />, index: true },
            { path: 'about-us', element: <AboutPage /> },
            { path: 'mcp', element: <McpPage /> },
            { path: 'contact-us', element: <Contact /> },
          ],
        },
        {
          element: <SimpleLayout />,
          children: [
            { path: 'pricing', element: <PricingPage /> },
            { path: 'payment', element: <PaymentPage /> },
          ],
        },
        {
          element: <CompactLayout />,
          children: [
            { path: 'coming-soon', element: <ComingSoonPage /> },
            { path: 'maintenance', element: <MaintenancePage /> },
            { path: '500', element: <Page500 /> },
            { path: '404', element: <Page404 /> },
            { path: '403', element: <Page403 /> },
          ],
        },
        {
          path: 'survey/:token',
          element: <RatingSurveyPage />,
        },
      ],
    },

    // Refund
    {
      path: 'refund',
      element: (
        <AuthenticatedRoutesProvider>
          <RefundHome />
        </AuthenticatedRoutesProvider>
      ),
    },
    {
      path: 'refund/purchases',
      element: (
        <AuthenticatedRoutesProvider>
          <RefundPurchases />
        </AuthenticatedRoutesProvider>
      ),
    },
    {
      path: 'refund/purchases/:id',
      element: (
        <AuthenticatedRoutesProvider>
          <RefundPurchase />
        </AuthenticatedRoutesProvider>
      ),
    },

    {
      path: 'subscriptions',
      element: (
        <AuthenticatedRoutesProvider>
          <SubscriberHome />
        </AuthenticatedRoutesProvider>
      ),
    },
    {
      path: 'subscriptions/list',
      element: (
        <AuthenticatedRoutesProvider>
          <SubscriberSubscriptions />
        </AuthenticatedRoutesProvider>
      ),
    },
    {
      path: 'subscriptions/list/:id/payment',
      element: (
        <AuthenticatedRoutesProvider>
          <SubscriberPayment />
        </AuthenticatedRoutesProvider>
      ),
    },
    {
      path: 'subscriptions/list/:id/payment/success',
      element: (
        <AuthenticatedRoutesProvider>
          <SubscriberPaymentSuccess />
        </AuthenticatedRoutesProvider>
      ),
    },
    {
      path: 'subscriptions/list/:id/history',
      element: (
        <AuthenticatedRoutesProvider>
          <SubscriberHistory />
        </AuthenticatedRoutesProvider>
      ),
    },

    // Members
    {
      element: (
        <AuthenticatedRoutesProvider>
          <CompactLayout />
        </AuthenticatedRoutesProvider>
      ),
      children: [
        {
          path: 'members/discord/finish-config',
          element: (
            <AuthGuard>
              <MembersDiscordFinishConfigPage />
            </AuthGuard>
          ),
        },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
